import { ComponentType } from '@angular/cdk/portal';
import { inject, Injectable, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import axios, { AxiosError } from 'axios';
import { format, isValid, parseISO } from 'date-fns';
import { hu } from 'date-fns/locale';
import { electronicFormatIBAN, isValidIBAN } from 'ibantools';
import { BehaviorSubject, debounceTime, filter, firstValueFrom, Subject } from 'rxjs';
import { getCompanyBlockDialogData } from 'src/app/dialogs/case/debtor-court-procedure-dialog/company-block-dialog.util';
import {
  DebtorCourtProcedureDialogComponent,
} from 'src/app/dialogs/case/debtor-court-procedure-dialog/debtor-court-procedure-dialog.component';
import {
  MultiUploadResultDialogComponent,
  MultiUploadResultDialogData,
  MultiUploadResultDialogResult,
  CurrentCaseDisplay,
  CurrentCaseClaimDisplay,
} from 'src/app/dialogs/case/multi-upload-result-dialog/multi-upload-result-dialog.component';
import { CompanySearchAutocompleteItem, CompanySearchService } from 'src/app/shared/company-search-service/company-search.service';
import { ErrorSnackbarComponent } from 'src/app/snackbars/error-snackbar/error-snackbar.component';
import { environment } from 'src/environments/environment';
import { CourtProcedureType } from 'src/types';
import { errorResponse } from 'src/types/error-response';
import { z } from 'zod';
import { ClientsService } from '../clients/clients.service';
import { InvoiceOcr, InvoiceOcrService } from '../invoice-ocr/invoice-ocr.service';
import { TransformFormDataService } from '../transform-form-data/transform-form-data.service';
import { UsersService } from '../users/users.service';
import { FormSaveQueueService } from './form-save-queue.service';
import { formComponents } from './form.components';
import { ClaimForm, DebtorForm, RootForm } from './form.controls';
import { BANK_ACCOUNT_FORM_NAME, CLAIMS_FORM_NAME, CLIENT_FORM_NAME, DEBTOR_FORM_NAME, Form, FormFactory, FormName, formNames, USER_FORM_NAME } from './form.factory';
import { BankAccount, bankAccountSchema, Campaign, campaignSchema, Claim, claimsSchema, Client, clientSchema, Debtor, debtorSchema, ProductType, SzamlazzhuInvoice, UserEmail, userEmailSchema } from './form.resources';
import {
  mapInvoiceOcrToClaim,
  patchClaimByInvoiceOcr,
} from './forms/claim';
import { mapInvoiceOcrToClient } from './forms/client';
import {
  hasIdentifiedDebtor,
  isDebtorMatchingInvoiceOcr,
  patchDebtorByInvoiceOcr,
} from './forms/debtor';
import { MAX_CLAIM_AMOUNT_HUF } from './validators/claim-amount-huf';

export type ParsedOcrInvoice = {
  claimForm: ClaimForm;
  fileName: string;
  amountLabel: string;
  dueDate: string;
  invoice: InvoiceOcr | null;
  file: File | null;
  isNewClaimForm: boolean;
};

export type OcrDebtorGroup = {
  debtor_name: string | null;
  debtor_tax_number: string | null;
  parsedInvoices: ParsedOcrInvoice[];
};

export type Case = {
  id: string;
  payee_case_reference_id: string | null;
  type: ProductType;
  partner_id: string | null;
  partner_logo_url: string | null;
  currency_iso: string;
  origin: string;
  client_id: string;
};

export type UpdateCofaceParams = {
  debtor_party_type_id: string;
  claims: {
    id: string;
    file?: File;
    coface_invoice_type_code: string;
    invoice_date: string;
  }[];
  terms_accepted: boolean;
};

type FeeItem = {
  label: string;
  fee_gross: number;
  fee_net: number;
  currency_iso: string;
};

export const CompanyEvaluationMessageType = {
  GENERIC: 'generic',
  ADVISORY: 'advisory',
  NO_HARD: 'no_hard_1',
  NO_ENFORCEMENT: 'no_hard_2',
  PARTNER_LAWYER: 'partner_lawyer',
} as const;

export type CompanyEvaluationMessageType =
  typeof CompanyEvaluationMessageType[keyof typeof CompanyEvaluationMessageType];

export const InsolvencyRiskLevel = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
} as const;

export type InsolvencyRiskLevel =
  typeof InsolvencyRiskLevel[keyof typeof InsolvencyRiskLevel];

export type CompanyEvaluation = {
  message_type: CompanyEvaluationMessageType;
  company_status: 'operating' | 'court_procedure' | 'suspended' | 'unknown' | null;
  revenue_huf: number | null;
  profit_huf: number | null;
  enforcements: { started_at: string; }[];
  insolvency_risk_level: InsolvencyRiskLevel | null;
  insolvency_analysis: string | null;
  recommend_enforcement: boolean;
  recommend_case_initiation: boolean;
  recommend_demand_letter_only: boolean;
  join_procedure_url: string | null;
  court_procedure_type: CourtProcedureType | null;
};

export const DEBTOR_FORM_STEP_NAME = 'debtor' as const;
export const CLIENT_FORM_STEP_NAME = 'client' as const;
export const PAYMENT_FORM_STEP_NAME = 'payment' as const;
export const PAYMENT_REDIRECT_FORM_STEP_NAME = 'payment-redirect' as const;

export const formStepNames = [
  DEBTOR_FORM_STEP_NAME,
  CLIENT_FORM_STEP_NAME,
  PAYMENT_FORM_STEP_NAME,
  PAYMENT_REDIRECT_FORM_STEP_NAME,
] as const;
export type FormStepName = typeof formStepNames[number];
export const nextFormStep: Record<FormStepName, FormStepName | null> = {
  [PAYMENT_REDIRECT_FORM_STEP_NAME]: null,
  [CLIENT_FORM_STEP_NAME]: 'payment',
  [DEBTOR_FORM_STEP_NAME]: 'client',
  [PAYMENT_FORM_STEP_NAME]: null,
};

type FormStepResponse = {
  forms: {
    name: FormName,
    data: Form[FormName],
  }[];
  payee_case: Case;
};

type SelectOptionalClaimsParams = {
  is_flat_rate_cost_claim_added: boolean;
  is_interest_added: boolean;
};

export type SyncSzamlazzhuInvoicesParams = {
  invoices: {
    invoice_id: string;
    file: File | null;
  }[];
};

type ClaimExchangeRates = Record<string, { unit: number; value: number; }>;

@Injectable({
  providedIn: 'root'
})
export class FormService {
  private snackbar = inject(MatSnackBar);
  private fb = inject(FormBuilder);
  private transformFormDataService = inject(TransformFormDataService);
  private router = inject(Router);
  private usersService = inject(UsersService);
  private translate = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private clientsService = inject(ClientsService);
  private invoiceOcrService = inject(InvoiceOcrService);
  private companySearchService = inject(CompanySearchService);
  private dialog = inject(MatDialog);
  private formSaveQueue = inject(FormSaveQueueService);

  private db?: IDBDatabase;
  private step?: FormStepName;
  readonly claimExchangeRates = new BehaviorSubject<ClaimExchangeRates | null>(null);
  readonly jurisdictionChange = new Subject<string>();
  readonly formChange = new Subject<void>();
  readonly debtorCollapsed = signal(false);
  readonly clientCollapsed = signal(false);

  private readonly claimBatch = new BehaviorSubject<ClaimForm[]>([]);

  forms: {
    name: FormName,
    data: Form[FormName],
  }[] = [];
  case?: Case;

  showTutorial = false;
  cameFromFeatures = false;

  debtorSaveRequiredError = false;
  clientSaveRequiredError = false;

  campaign: Campaign = {};

  readonly form: RootForm = new FormGroup({});
  caseId?: 'guest' | string & {};
  get isGuest(): boolean { return this.caseId === 'guest'; }
  get clientAllowsForm(): boolean {
    if (this.isGuest) {
      return true;
    }
    return this.clientsService.selectedClientSubject.value?.allow_form !== false;
  }

  readonly saving = new BehaviorSubject<number>(0);
  readonly justSaved = new BehaviorSubject<number>(0);
  readonly paymentLoading = new BehaviorSubject<boolean>(false);

  constructor() {
    this.translate.onLangChange.subscribe(async () => {
      const stepsRequireRefetching: FormName[] = [
        'summary',
      ];
      if (this.forms.some(({ name }) => stepsRequireRefetching.includes(name))) {
        await this.setFormData(this.step);
      }
    });

    this.route.queryParams
      .subscribe(queryParams => {
        const showTutorial = queryParams.showTutorial;
        this.showTutorial = !!showTutorial;
      });

    this.claimBatch
      .pipe(
        debounceTime(1000),
        filter(forms => forms.length > 0),
      )
      .subscribe(async forms => {
        this.claimBatch.next([]);
        await this.flushClaimBatch(forms);
      });
  }

  async redirectToStep(step: FormStepName, caseId = this.caseId): Promise<void> {
    await this.router.navigate([`case/${caseId}/${step}`], {
      queryParamsHandling: 'merge',
      queryParams: {
        origin: this.case?.origin,
      },
      replaceUrl: true,
    });
  }

  async redirectToCases(payee_case_reference_id = this.case?.payee_case_reference_id): Promise<void> {
    await this.router.navigate(['/user/cases'], {
      queryParams: {
        payee_case_reference_id,
      },
    });
  }

  async redirectAfterCaseStarted(): Promise<void> {
    await this.router.navigate(['/user/case-started'], {
      queryParams: {
        payee_case_reference_id: this.case?.payee_case_reference_id,
        type: this.case?.type,
        partner_id: this.case?.partner_id,
        origin: this.case?.origin,
      },
      queryParamsHandling: 'merge',
    });

    if (this.case) {
      axios.post(`${environment.baseUrl}/api/case-form/gtm-start-sent/${this.case.id}`)
        .catch(e => console.error('Error while saving gtm start event', e));
    }
  }

  setCaseId(caseId: string): FormService {
    this.caseId = caseId;
    return this;
  }

  convertClaimAmountToHuf(
    amount: number,
    currencyIso: string,
  ): number {
    if (!amount || Number.isNaN(amount)) {
      return 0;
    }
    if (!currencyIso || currencyIso === 'HUF') {
      return amount;
    }

    const r = this.claimExchangeRates.value?.[currencyIso];
    if (!r) {
      return amount;
    }

    return Math.round(amount * (r.value / r.unit));
  }

  getMaxClaimOriginalAmountInCurrency(currencyIso: string): number {
    const maxHuf = MAX_CLAIM_AMOUNT_HUF;
    if (!currencyIso || currencyIso === 'HUF') {
      return maxHuf;
    }
    const r = this.claimExchangeRates.value?.[currencyIso];
    if (!r) {
      return maxHuf;
    }
    const ratio = r.value / r.unit;
    let n = Math.floor(maxHuf / ratio);
    while (n > 0 && Math.round(n * ratio) > maxHuf) {
      n--;
    }
    return n;
  }

  async setFormData(step?: FormStepName): Promise<void> {
    this.step = step;
    if (!step) {
      return;
    }
    if (this.caseId === 'guest') {
      await this.loadFormDataFromLocalSave(step);
      if (this.forms.some(({ name }) => name === CLAIMS_FORM_NAME)) {
        await this.ensureClaimExchangeRatesLoaded();
      }
      this.router.navigate([], {
        queryParamsHandling: 'merge',
        queryParams: {
          origin: this.case?.origin,
        },
      });
      return;
    }

    const url = `${environment.baseUrl}/api/case-form/${this.caseId}/step/${step}`;
    const result = await axios.get<FormStepResponse>(url);

    const client = await this.clientsService.getClient(result.data.payee_case.client_id);
    if (!this.clientsService.selectedClientSubject.value) {
      this.clientsService.setSelectedClient(client);
    }

    this.forms = result.data.forms;
    this.case = result.data.payee_case;
    if (this.forms.some(({ name }) => name === CLAIMS_FORM_NAME)) {
      await this.ensureClaimExchangeRatesLoaded();
    }

    if (
      step === 'debtor'
      && client.ongoing_cases === 0
      && client.closed_cases === 0
      && result.data.payee_case.partner_id !== 'szamlazzhu'
      && !this.cameFromFeatures
    ) {
      this.setFormGroup();
      if (this.form.invalid) {
        await this.openTutorial();
      }
    }
    this.router.navigate([], {
      queryParamsHandling: 'merge',
      queryParams: {
        origin: this.case?.origin,
      },
    });
  }

  getFormComponents(): { name: FormName, component: ComponentType<unknown>; }[] {
    const components: { name: FormName, component: ComponentType<unknown>; }[] = [];
    for (const { name } of this.forms) {
      const component = formComponents[name];
      if (component) {
        components.push({
          component,
          name: name,
        });
      }
    }
    return components;
  }

  setFormGroup(): FormService {
    Object.keys(this.form.controls).forEach(key => this.form.removeControl(key, { emitEvent: false }));

    for (const { data, name } of this.forms) {
      const form = new FormFactory(this, this.fb, data).getForm(name);

      this.form.addControl(name, form, { emitEvent: false });
    };

    this.formChange.next();

    return this;
  }

  async closeTutorial(): Promise<void> {
    this.cameFromFeatures = true;
    await this.router.navigate([], {
      queryParamsHandling: 'merge',
      queryParams: {
        showTutorial: null,
      },
    });

    setTimeout(() => {
      document.querySelector('app-case-form')?.scroll(0, 0);
    });
  }

  async openTutorial(): Promise<void> {
    await this.router.navigate([], {
      queryParamsHandling: 'merge',
      queryParams: {
        showTutorial: 1,
      },
    });
  }

  async changeJurisdiction(
    jurisdiction: string,
    options?: { emitChange?: boolean; },
  ): Promise<void> {
    await this.save(async () => {
      if (this.isGuest) {
        this.saveFormLocally('jurisdiction', {
          id: '1',
          jurisdiction,
        });
        return;
      }

      const url = `${environment.baseUrl}/api/case-form/${this.caseId}/debtor/change-jurisdiction`;
      await axios.post(
        url,
        { jurisdiction },
      );
    });

    if (options?.emitChange !== false) {
      this.jurisdictionChange.next(jurisdiction);
    }
  }

  async upsertDebtor(params: Debtor): Promise<void> {
    return this.formSaveQueue.enqueue(this.saveQueueKey('debtor'), async () => {
      await this.save(async () => {
        if (this.isGuest) {
          this.saveFormLocally('debtor', params);
          return;
        }

        const url = `${environment.baseUrl}/api/case-form/${this.caseId}/debtor`;
        await axios.post(url, params);
      });
    });
  }

  async getCompanyEvaluation(taxNumber: string, debtorName: string): Promise<CompanyEvaluation | null> {
    const taxNumberRoot = taxNumber.replace(/\D/g, '').slice(0, 8);
    if (taxNumberRoot.length !== 8) {
      return null;
    }

    const url = `${environment.baseUrl}/api/case-form/company-evaluation`;
    const response = await axios.get<CompanyEvaluation>(url, {
      params: {
        tax_number: taxNumberRoot,
        debtor_name: debtorName,
      },
    });

    return response.data;
  }

  upsertClaim(form: ClaimForm): void {
    const batch = this.claimBatch.value;
    if (batch.includes(form)) {
      return;
    }

    batch.push(form);
    this.claimBatch.next(batch);
  }

  private async flushClaimBatch(forms: ClaimForm[]): Promise<void> {
    const claims = forms.map(form => {
      const value = form.getRawValue();
      const dueDateAt = form.controls.due_date_at.value;
      const formattedDueDateAt = dueDateAt instanceof Date
        ? format(dueDateAt, 'yyyy-MM-dd')
        : dueDateAt;

      return {
        id: value.id || null,
        original_amount: +value.original_amount,
        currency_iso: value.currency_iso,
        due_date_at: formattedDueDateAt,
        invoice_number: value.invoice_number,
        issued_at: value.issued_at,
        claim_type_internal_id: value.claim_type_internal_id,
        file: value.file instanceof File
          ? value.file
          : undefined,
      };
    });

    try {
      await this.formSaveQueue.enqueue(this.saveQueueKey('claims'), async () => {
        await this.save(async () => {
          if (this.isGuest) {
            await Promise.all(claims.map((claim, index) => this.saveFormLocally('claims', {
              ...claim,
              id: claim.id ?? forms[index].controls.id.value,
            })));
            return;
          }

          const params = this.transformFormDataService.objectToFormData({ claims });
          const url = `${environment.baseUrl}/api/case-form/${this.caseId}/claim`;
          const result = await axios.post<{ claims: Claim[]; }>(url, params);

          result.data.claims.forEach((claim, index) => {
            const form = forms[index];
            if (!form) {
              return;
            }

            form.patchValue(
              {
                id: claim.id,
                file: claim.file,
              },
              {
                emitEvent: false,
                onlySelf: true,
              },
            );
          });
        });
      });
    } catch (error) {
      console.error('Error while saving claims', error);
      if (error instanceof AxiosError && error.response?.status === 422) {
        const errorData = errorResponse.safeParse(error.response.data);
        if (errorData.success) {
          Object.entries(errorData.data.errors).forEach(([path, errors]) => {
            const match = path.match(/^claims\.(\d+)\.(.+)$/);
            if (!match) {
              return;
            }
            forms[+match[1]]?.get(match[2])?.setErrors({
              unknown: errors[0],
            });
          });
        }
      }

      forms.forEach(form => form.setErrors({ unknown: true }));
    }
  }

  async deleteClaim(id: string): Promise<void> {
    await this.save(async () => {
      if (this.isGuest) {
        this.deleteFromLocalStorage('claims', id);
        return;
      }

      const url = `${environment.baseUrl}/api/case-form/${this.caseId}/claim/${id}`;
      await axios.delete(url);
    });
  }

  async parseInvoiceFileForClaim(file: File, claimForm: ClaimForm): Promise<void> {
    const debtorForm = this.getDebtorForm();
    if (!debtorForm) {
      return;
    }

    const currentDebtor = debtorForm.value as Debtor;

    let invoice: InvoiceOcr;
    try {
      invoice = await this.invoiceOcrService.parseInvoice(file);
    } catch (error) {
      this.showInvoiceOcrError(error);
      return;
    }

    const parsedInvoices = [
      this.buildParsedOcrInvoice(claimForm, invoice, file, false),
    ];

    if (await this.openOcrDebtorDialogIfNeeded(currentDebtor, parsedInvoices, [])) {
      return;
    }

    await this.ensureJurisdictionForInvoiceOcr(invoice);
    await this.applyParsedInvoicesToClaims(parsedInvoices);
    let companyData: CompanySearchAutocompleteItem | null = null;
    try {
      const [appliedCompanyData] = await Promise.all([
        this.applyDebtorFromInvoiceOcr(invoice),
        this.patchClientFormFromInvoiceOcr(invoice),
      ]);
      companyData = appliedCompanyData;
    } catch (error: unknown) {
      console.error('Error while patching form by invoice', error);
    }

    await this.finalizeDebtorCollapseAfterCompanySearch(debtorForm, companyData);
  }

  async parseInvoiceFiles(files: File[]): Promise<void> {
    const debtorForm = this.getDebtorForm();
    if (!debtorForm?.get('jurisdiction')?.value) {
      return;
    }

    const currentDebtor: Debtor = debtorForm.getRawValue();

    const ocrResults = await Promise.allSettled(
      files.map(uploadedFile => this.invoiceOcrService.parseInvoice(uploadedFile)),
    );

    const rejectedFiles: string[] = [];
    const parsedInvoices: ParsedOcrInvoice[] = [];
    const reservedClaimForms = new Set<ClaimForm>();
    let invoiceOcrError: unknown;

    for (let i = 0; i < ocrResults.length; ++i) {
      const result = ocrResults[i];
      const file = files[i];
      if (!result || !file) {
        break;
      }

      if (result.status === 'rejected') {
        rejectedFiles.push(file.name);
        invoiceOcrError = result.reason;
        continue;
      }

      const invoice = result.value;
      const reservedClaim = this.findOrCreateClaimFormForInvoice(
        invoice,
        file,
        false,
        reservedClaimForms,
      );
      if (!reservedClaim) {
        continue;
      }

      parsedInvoices.push(this.buildParsedOcrInvoice(
        reservedClaim.claimForm,
        invoice,
        file,
        reservedClaim.isNewClaimForm,
      ));
    }

    if (invoiceOcrError) {
      this.showInvoiceOcrError(invoiceOcrError);
      return;
    }

    if (await this.openOcrDebtorDialogIfNeeded(currentDebtor, parsedInvoices, rejectedFiles)) {
      return;
    }

    const ocrParsed = this.getOcrParsedInvoices(parsedInvoices);
    const firstOcrParsed = ocrParsed[0];
    if (firstOcrParsed?.invoice) {
      await this.ensureJurisdictionForInvoiceOcr(firstOcrParsed.invoice);
    }
    await this.applyParsedInvoicesToClaims(ocrParsed);
    let companyData: CompanySearchAutocompleteItem | null = null;
    if (firstOcrParsed?.invoice) {
      try {
        const [appliedCompanyData] = await Promise.all([
          this.applyDebtorFromInvoiceOcr(firstOcrParsed.invoice),
          this.patchClientFormFromInvoiceOcr(firstOcrParsed.invoice),
        ]);
        companyData = appliedCompanyData;
      } catch (error: unknown) {
        console.error('Error while patching form by invoice', error);
      }
    }

    await this.finalizeDebtorCollapseAfterCompanySearch(debtorForm, companyData);
  }

  async updateClient(params: Client): Promise<void> {
    return this.formSaveQueue.enqueue(this.saveQueueKey('client'), async () => {
      await this.save(async () => {
        if (this.isGuest) {
          this.saveFormLocally('client', params);
          return;
        }

        const url = `${environment.baseUrl}/api/case-form/${this.caseId}/client`;
        await axios.post(url, params);
      });
    });
  }

  async assignClient(clientId: string): Promise<void> {
    if (this.isGuest || !this.step) {
      return;
    }

    await this.formSaveQueue.enqueue(this.saveQueueKey('bank-account'), async () => {});

    return this.formSaveQueue.enqueue(this.saveQueueKey('client-assign'), async () => {
      await this.save(async () => {
        const url = `${environment.baseUrl}/api/case-form/${this.caseId}/client/assign`;
        await axios.post(url, { client_id: clientId });
        await this.setFormData(this.step!);
        const assignedClient = await this.clientsService.getClient(clientId);
        this.clientsService.setSelectedClient(assignedClient);
        this.setFormGroup();
      });
    });
  }

  async upsertBankAccount(params: BankAccount): Promise<void> {
    return this.formSaveQueue.enqueue(this.saveQueueKey('bank-account'), async () => {
      await this.save(async () => {
        if (this.isGuest) {
          this.saveFormLocally('bank-account', params);
          return;
        }

        const url = `${environment.baseUrl}/api/case-form/${this.caseId}/bank-account`;
        await axios.post(url, params);
      });
    });
  }

  async selectOptionalClaims(params: SelectOptionalClaimsParams): Promise<void> {
    if (this.isGuest) {
      return;
    }

    await this.save(async () => {
      const url = `${environment.baseUrl}/api/case-form/${this.caseId}/payment/select-optional-claims`;
      const result = await axios.post<FormStepResponse>(url, params);

      this.forms = result.data.forms;
      this.setFormGroup();
    });
  }

  async changeProduct(product_type: ProductType): Promise<void> {
    if (!this.case) {
      return;
    }

    if (this.isGuest) {
      this.case.type = product_type;
      return;
    }

    await this.save(async () => {
      const url = `${environment.baseUrl}/api/case-form/${this.caseId}/payment/change-product/${product_type}`;
      const result = await axios.post<FormStepResponse>(url);

      this.forms = result.data.forms;
      this.case!.type = product_type;
      this.setFormGroup();
    });
  }

  async updateCofaceForm(params: UpdateCofaceParams): Promise<void> {
    if (this.isGuest) {
      return;
    }

    await this.save(async () => {
      const url = `${environment.baseUrl}/api/case-form/${this.caseId}/payment/coface-form`;
      const data = this.transformFormDataService.objectToFormData(
        params,
        {
          allowEmptyArrays: false,
          booleansAsIntegers: true,
          indices: true,
        },
      );
      const result = await axios.post<FormStepResponse>(url, data);

      this.forms = result.data.forms;
      this.setFormGroup();
    });
  }

  async upsertDebtorContacts(email: string): Promise<void> {
    if (this.isGuest) {
      return;
    }

    if (this)
      await this.save(async () => {
        const url = `${environment.baseUrl}/api/case-form/${this.caseId}/payment/debtor-contacts`;
        await axios.post(url, {
          email,
        });
      });
  }

  async syncSzamlazzhuInvoices(params: SyncSzamlazzhuInvoicesParams): Promise<SzamlazzhuInvoice[]> {
    return this.formSaveQueue.enqueue(this.saveQueueKey('szamlazzhu-invoices'), async () => {
      return await this.save(async () => {
        const url = `${environment.baseUrl}/api/case-form/${this.caseId}/szamlazzhu/invoice`;
        const formData = this.transformFormDataService.objectToFormData(params);
        const result = await axios.post<{ invoices: SzamlazzhuInvoice[]; }>(url, formData);
        return result.data.invoices;
      });
    });
  }

  async updateGuestUserEmail(params: UserEmail): Promise<void> {
    if (!this.isGuest) {
      return;
    }

    await this.save(async () => {
      this.saveFormLocally('user', params);
    });
  }

  async saveCampaignLocally(): Promise<void> {
    await this.saveFormLocally('campaign', {
      id: 'campaign',
      ...this.campaign,
    });
  }

  async registerGuest(): Promise<void> {
    if (this.form.invalid) {
      console.warn('Invalid form');
      return;
    }

    const jurisdiction = await this.getGuestJurisdiction();

    const client = this.form.get(CLIENT_FORM_NAME)!.value as Client;
    if (client.birth_date instanceof Date) {
      client.birth_date = format(client.birth_date, 'yyyy-MM-dd');
    }

    const userEmail = this.form.get(USER_FORM_NAME)!.value as UserEmail;

    const bank_account = this.form.get(BANK_ACCOUNT_FORM_NAME)!.value as BankAccount;
    const electronicBankAccount = electronicFormatIBAN(bank_account.account_number || bank_account.iban!)!;
    if (isValidIBAN(electronicBankAccount)) {
      bank_account.iban = electronicBankAccount;
      bank_account.account_number = null;
    } else {
      bank_account.iban = null;
      bank_account.account_number = electronicBankAccount;
    }

    const rawSavedDebtor = await this.getSavedFormFromLocalSave(DEBTOR_FORM_NAME);
    const debtor = debtorSchema.safeParse(rawSavedDebtor, { path: [DEBTOR_FORM_NAME] });
    if (!debtor.success) {
      console.warn('Errors while loading debtor', {
        error: debtor.error?.issues,
      });
      await this.redirectToStep('debtor', 'guest');
      return;
    }
    if (debtor.data.address) {
      debtor.data.address.country_iso = jurisdiction;
    }

    const rawSavedClaims = await this.getSavedFormFromLocalSave('claims');
    const claims = claimsSchema.safeParse(rawSavedClaims, { path: ['claims'] });
    if (!claims.success) {
      await this.redirectToStep('debtor', 'guest');
      return;
    }

    const url = `${environment.baseUrl}/api/case-form/register-guest`;
    const params = this.transformFormDataService.objectToFormData({
      user: userEmail,
      client,
      claims: claims.data,
      debtor: debtor.data,
      bank_account,
      campaign: this.campaign,
      jurisdiction,
    });
    params.forEach((_, key) => {
      if (key.includes('[id]')) {
        params.delete(key);
      }
    });
    const result = await axios.post<{
      payee_case: Case;
    }>(url, params);

    this.case = result.data.payee_case;
    this.caseId = result.data.payee_case.id;
    this.forms = [];

    await this.usersService.setUser();
    await this.redirectToStep('payment', this.case.id);
  }

  async startWithCard(): Promise<void> {
    if (this.paymentLoading.value) {
      return;
    }

    await this.save(async () => {
      try {
        this.paymentLoading.next(true);
        const url = `${environment.baseUrl}/api/case-form/${this.caseId}/payment/start-with-card`;
        const result = await axios.post<{ payment_url: string; }>(url);
        window.location.href = result.data.payment_url;
      } finally {
        this.paymentLoading.next(false);
      }
    });
  }

  /**
   * @returns {Promise<string | null>} pro forma invoice number or null
   */
  async startWithTransfer(): Promise<string | null> {
    if (this.paymentLoading.value) {
      return null;
    }

    return this.save(async () => {
      try {
        this.paymentLoading.next(true);
        const url = `${environment.baseUrl}/api/case-form/${this.caseId}/payment/start-with-transfer`;
        const response = await axios.post<{ pro_forma_invoice_number: string | null; }>(url);
        this.pushPurchaseEventToDataLayer('transfer');

        return response.data.pro_forma_invoice_number;
      } finally {
        this.paymentLoading.next(false);
      }
    });
  }

  async startInstantly(): Promise<void> {
    if (this.paymentLoading.value) {
      return;
    }

    await this.save(async () => {
      try {
        this.paymentLoading.next(true);
        const url = `${environment.baseUrl}/api/payee-case/${this.caseId}/start-instantly`;
        await axios.post(url);
        this.pushPurchaseEventToDataLayer('instant');
        await this.redirectAfterCaseStarted();
      } finally {
        this.paymentLoading.next(false);
      }
    });
  }

  pushPurchaseEventToDataLayer(payment_type: 'card' | 'transfer' | 'instant' | 'unknown'): void {
    if (!this.case || !this.case.id || !window.dataLayer) {
      return;
    }

    if (payment_type === 'instant' || payment_type === 'unknown') {
      const event = this.getPurchaseEventOfFee([{
        currency_iso: 'HUF',
        fee_gross: 0,
        fee_net: 0,
        label: 'Fizetési felszólítás',
      }]);

      window.dataLayer.push({
        ecommerce: null,
      });
      window.dataLayer.push(event);

      return;
    }

    axios.get(`${environment.baseUrl}/api/case-form/${this.case.id}/paid-fees`)
      .then(result => {
        const fees = result.data.fees as FeeItem[];
        const event = this.getPurchaseEventOfFee(fees, payment_type);

        window.dataLayer.push({
          ecommerce: null,
        });
        window.dataLayer.push(event);
      })
      .catch(e => console.error('Error while getting paid fees', e));
  }

  async getGuestJurisdiction(): Promise<string> {
    const rawJurisdiction = await this.getSavedFormFromLocalSave('jurisdiction') as string;
    const savedJurisdiction = z.object({
      jurisdiction: z.string().length(2),
    }).safeParse(rawJurisdiction);
    return savedJurisdiction.data?.jurisdiction ?? 'HU';
  }

  async getSavedFormFromLocalSave<T extends FormName | 'jurisdiction'>(step: T): Promise<
    T extends 'claims' ? unknown[] | null :
    unknown | null
  > {
    await this.openDatabaseConnection();
    if (!this.db) {
      return null;
    }

    const transaction = this.db.transaction(step, 'readonly');
    const store = transaction.objectStore(step);

    return new Promise(res => {
      const request = store.getAll();
      request.onerror = (event: Event) => {
        const target = event.target as IDBRequest;
        console.warn('Error while getting form data', {
          step,
          error: target.error,
        });

        res(null);
      };
      request.onsuccess = () => {
        if (step === 'claims') {
          res(request.result as unknown[]);
        } else {
          res(request.result?.[0] ?? null);
        }
      };
    });
  }

  private async applyOcrDebtorGroup(selectedGroup: OcrDebtorGroup): Promise<void> {
    const ocrParsed = this.getOcrParsedInvoices(selectedGroup.parsedInvoices);
    if (ocrParsed.length === 0) {
      return;
    }

    const representativeInvoice = ocrParsed[0].invoice!;

    await this.clearAllClaims(false);
    await this.ensureJurisdictionForInvoiceOcr(
      representativeInvoice,
      selectedGroup.debtor_tax_number,
    );

    // Jurisdiction change reloads the form — always use a fresh reference after.
    const debtorForm = this.getDebtorForm();
    if (!debtorForm) {
      return;
    }

    await this.clearAllClaims(false);
    await this.applyParsedInvoicesToClaims(ocrParsed);
    const companyData = await this.applyDebtorFromInvoiceOcr(
      representativeInvoice,
      selectedGroup.debtor_tax_number,
    );

    const firstOcrParsed = ocrParsed[0];
    if (firstOcrParsed?.invoice) {
      await this.patchClientFormFromInvoiceOcr(firstOcrParsed.invoice);
    }

    await this.finalizeDebtorCollapseAfterCompanySearch(debtorForm, companyData);
  }

  private async applyOcrClaimsKeepingDebtor(
    invoices: ParsedOcrInvoice[],
  ): Promise<void> {
    const debtorForm = this.getDebtorForm();
    if (!debtorForm) {
      return;
    }

    await this.applyParsedInvoicesToClaims(this.getOcrParsedInvoices(invoices));
    debtorForm.updateValueAndValidity({ emitEvent: true });
    this.debtorCollapsed.set(debtorForm.valid);
  }

  private async applyOcrCurrentDebtorSelection(
    keepClaimForms: Set<ClaimForm>,
  ): Promise<void> {
    const debtorForm = this.getDebtorForm();
    if (!debtorForm) {
      return;
    }

    await this.removeClaimsExcept(keepClaimForms);
    debtorForm.updateValueAndValidity({ emitEvent: true });
    this.debtorCollapsed.set(debtorForm.valid);
  }

  private getNonEmptyClaimForms(): ClaimForm[] {
    const claims = this.form.get(CLAIMS_FORM_NAME) as FormArray;
    if (!claims) {
      return [];
    }

    return claims.controls
      .filter((control) => !this.isEmptyClaimForm(control as ClaimForm))
      .map((control) => control as ClaimForm);
  }

  private getPurchaseEventOfFee(
    fees: FeeItem[],
    payment_type?: 'card' | 'transfer',
  ) {
    const netSum = fees.reduce((sum, f) => f.fee_net + sum, 0);
    const taxSum = fees.reduce((sum, f) => f.fee_gross - f.fee_net + sum, 0);

    const event = {
      event: "purchase",
      ecommerce: {
        transaction_id: this.case?.payee_case_reference_id,
        value: netSum,
        tax: taxSum,
        currency: fees[0]?.currency_iso ?? 'HUF',
        payment_type,
        customer_type: this.case?.origin === 'guest'
          ? 'new'
          : 'returning',
        items: fees.map(f => ({
          item_name: f.label,
          price: f.fee_net,
          quantity: 1,
        })),
      }
    };

    return event;
  }

  private saveQueueKey(scope: string, id?: string): string {
    const caseId = this.caseId ?? 'guest';
    return id ? `${caseId}:${scope}:${id}` : `${caseId}:${scope}`;
  }

  private async save<T>(callback: () => Promise<T>, retry = 2): Promise<T> {
    try {
      this.saving.next(this.saving.value + 1);

      try {
        const result = await callback();

        this.justSaved.next(this.justSaved.value + 1);
        setTimeout(() => this.justSaved.next(this.justSaved.value - 1), 2500);

        return result;
      } catch (error: AxiosError | unknown) {
        if (error instanceof AxiosError) {
          if (
            error.response?.status
            && (
              // Server
              error.response.status >= 500
              // Validation
              || error.response.status === 422
              // Rate limit
              || error.response.status === 429
            )
          ) {
            throw error;
          }
        }

        if (retry <= 0) {
          throw error;
        }

        return await this.save(callback, retry - 1);
      }
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 429) {
          this.snackbar.open(
            this.translate.instant('ERROR_MESSAGES.TOO_MANY_REQUESTS'),
            this.translate.instant('OK'),
            { duration: 10_000 },
          );
        } else if (error.response?.status === 422 && error.response?.data?.message) {
          this.snackbar.open(
            error.response.data.message,
            this.translate.instant('OK'),
            { duration: 10_000 },
          );
        } else {
          console.error('Network error while saving', error.response?.data);
          this.snackbar.open(
            this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
            this.translate.instant('OK'),
            { duration: 10_000 },
          );
        }
      } else {
        console.error('Unknown error while saving', error);
        this.snackbar.openFromComponent(ErrorSnackbarComponent, {
          data: {
            message: this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
            action: this.translate.instant('OK'),
          },
          duration: 10_000,
        });
      }

      throw error;
    } finally {
      setTimeout(() => this.saving.next(this.saving.value - 1), 200);
    }
  }

  private async openDatabaseConnection(): Promise<void> {
    if (this.db) {
      return;
    }

    return new Promise(res => {
      const request = indexedDB.open('case-form-db', 4);

      // Database is created
      request.onupgradeneeded = event => {
        this.db = request.result;

        if (event.oldVersion < 1) {
          this.db.createObjectStore("debtor", { keyPath: 'id' });
          this.db.createObjectStore("claims", { keyPath: 'id' });
          this.db.createObjectStore("client", { keyPath: 'id' });
          this.db.createObjectStore("bank-account", { keyPath: 'id' });
        }

        if (event.oldVersion < 2) {
          this.db.createObjectStore("user", { keyPath: 'id' });
        }

        if (event.oldVersion < 3) {
          this.db.createObjectStore("campaign", { keyPath: 'id' });
        }

        if (event.oldVersion < 4) {
          this.db.createObjectStore("jurisdiction", { keyPath: 'id' });
        }

        for (const name of formNames) {
          if (!this.db.objectStoreNames.contains(name)) {
            this.db.createObjectStore(name, { keyPath: 'id' });
          }
        }
      };

      request.onsuccess = () => {
        this.db = request.result;
        res();
      };

      request.onerror = (event: Event) => {
        const target = event.target as IDBOpenDBRequest;
        console.error('Error while opening database', {
          error: target.error,
        });
        res();
      };
    });
  }

  private async loadFormDataFromLocalSave(step: FormStepName): Promise<void> {
    this.forms.length = 0;

    this.case = {
      id: 'guest',
      partner_id: null,
      partner_logo_url: null,
      payee_case_reference_id: null,
      type: 'hard_1',
      // todo get from backend somehow
      currency_iso: '',
      origin: 'guest',
      client_id: '',
    };

    const rawSavedCampaign = await this.getSavedFormFromLocalSave('campaign');
    const campaign = campaignSchema.safeParse(rawSavedCampaign);
    if (campaign.success) {
      this.campaign = campaign.data;
    }

    const jurisdiction = await this.getGuestJurisdiction();

    if (step === 'debtor') {
      const rawSavedDebtor = await this.getSavedFormFromLocalSave('debtor');
      const savedDebtor = debtorSchema.safeParse(rawSavedDebtor);
      const debtor: Debtor = savedDebtor.success
        ? savedDebtor.data
        : {
          address: {
            country_iso: jurisdiction,
            postcode: '',
            settlement: '',
            street: '',
          },
          debtor_type: 'org',
          name: '',
          // party_type_id: null,
          representative: null,
          tax_number: '',
          is_editable: true,
          jurisdiction: jurisdiction,
        };
      debtor.jurisdiction = jurisdiction;

      if (!savedDebtor.success && rawSavedDebtor) {
        this.deleteFromLocalStorage('debtor');
        console.warn('Error while loading debtor from local storage', {
          errors: savedDebtor.error,
        });
      }

      const rawSavedClaims = await this.getSavedFormFromLocalSave('claims');
      const savedClaims = claimsSchema.safeParse(rawSavedClaims);
      const claims = savedClaims.success
        ? savedClaims.data
        : [];
      if (!savedClaims.success && rawSavedClaims) {
        this.deleteFromLocalStorage('claims');
        console.warn('Error while loading claims from local storage', {
          errors: savedClaims.error,
        });
      }

      this.forms.push({
        data: debtor,
        name: 'debtor',
      });
      this.forms.push({
        data: claims,
        name: 'claims',
      });
    } else if (step === 'client') {
      const rawSavedClient = await this.getSavedFormFromLocalSave('client');
      const savedClient = clientSchema.safeParse(rawSavedClient);
      const client: Client = savedClient.success
        ? savedClient.data
        : {
          address: {
            country_iso: 'HU',
            postcode: '',
            settlement: '',
            street: '',
          },
          birth_date: null,
          birth_place: '',
          client_type: 'org',
          mother_name: '',
          name: '',
          party_type_id: null,
          representative: null,
          tax_number: '',
        };
      if (!savedClient.success && rawSavedClient) {
        this.deleteFromLocalStorage('client');
        console.warn('Error while loading client from local storage', {
          errors: savedClient.error,
          data: rawSavedClient,
        });
      }

      const rawSavedBankAccount = await this.getSavedFormFromLocalSave('bank-account');
      const savedBankAccount = bankAccountSchema.safeParse(rawSavedBankAccount);
      const bankAccount: BankAccount = savedBankAccount.success
        ? savedBankAccount.data
        : {
          account_number: '',
          iban: '',
        };
      if (!savedBankAccount.success && rawSavedBankAccount) {
        this.deleteFromLocalStorage('bank-account');
        console.warn('Error while loading client bank account from local storage', {
          errors: savedBankAccount.error,
          data: rawSavedBankAccount,
        });
      }

      const rawSavedUserEmail = await this.getSavedFormFromLocalSave('user');
      const savedUserEmail = userEmailSchema.safeParse(rawSavedUserEmail);
      const userEmail: UserEmail = savedUserEmail.success
        ? savedUserEmail.data
        : {
          email: '',
          terms_of_service: false,
        };
      if (!savedUserEmail.success && rawSavedUserEmail) {
        this.deleteFromLocalStorage('user');
        console.warn('Error while loading user from local storage', {
          errors: savedUserEmail.error,
          data: rawSavedUserEmail,
        });
      }

      this.forms.push({
        data: client,
        name: 'client',
      });
      this.forms.push({
        data: userEmail,
        name: 'user',
      });
      this.forms.push({
        data: bankAccount,
        name: 'bank-account',
      });
    } else {
      console.warn('Step not allowed for guest');
      this.router.navigateByUrl(`/case/guest/debtor`);
    }
  }

  private async saveFormLocally<T extends { id?: string | null; }>(
    step: FormName | 'jurisdiction',
    data: T,
  ): Promise<void> {
    for (let tries = 0; tries < 3; ++tries) {
      await this.openDatabaseConnection();
      if (!this.db) {
        return;
      }

      const transaction = this.db.transaction(step, 'readwrite');
      const store = transaction.objectStore(step);
      try {
        store.put(data);
        return;
      } catch (error) {
        console.error('Error while saving guest form', { error, data, id: data.id, step });
      }
    }
  }

  private async deleteFromLocalStorage(step: FormName, id?: string): Promise<void> {
    await this.openDatabaseConnection();
    if (!this.db) {
      return;
    }

    const transaction = this.db.transaction(step, 'readwrite');
    const store = transaction.objectStore(step);

    if (id) {
      store.delete(id);
    } else {
      store.clear();
    }
  }

  private findOrCreateClaimFormForInvoice(
    invoice: InvoiceOcr,
    file: File,
    patch = true,
    reservedClaimForms: Set<ClaimForm> = new Set(),
  ): { claimForm: ClaimForm; isNewClaimForm: boolean; } | undefined {
    const claims = this.form.get(CLAIMS_FORM_NAME) as FormArray;
    if (!claims) {
      return undefined;
    }

    for (const claimForm of claims.controls) {
      const claim = claimForm.value as Claim;
      if (claim.invoice_number === invoice.invoice_number) {
        if (patch) {
          patchClaimByInvoiceOcr(claimForm as ClaimForm, invoice, file);
        }

        return {
          claimForm: claimForm as ClaimForm,
          isNewClaimForm: false,
        };
      }
    }

    const claim = mapInvoiceOcrToClaim(invoice, file);
    const emptyClaimForm = this.findEmptyClaimForm(claims, reservedClaimForms);
    if (emptyClaimForm) {
      if (patch) {
        emptyClaimForm.patchValue(claim);
      }

      reservedClaimForms.add(emptyClaimForm);
      return {
        claimForm: emptyClaimForm,
        isNewClaimForm: false,
      };
    }

    const newClaimForm = (new FormFactory(this, this.fb)).getForm('claim') as ClaimForm;
    if (patch) {
      newClaimForm.patchValue(claim);
      claims.push(newClaimForm);
    }

    reservedClaimForms.add(newClaimForm);
    return {
      claimForm: newClaimForm,
      isNewClaimForm: true,
    };
  }

  private findEmptyClaimForm(
    claims: FormArray,
    reservedClaimForms: Set<ClaimForm> = new Set(),
  ): ClaimForm | undefined {
    return claims.controls.find((control) => {
      const claimForm = control as ClaimForm;
      if (reservedClaimForms.has(claimForm)) {
        return false;
      }

      return this.isEmptyClaimForm(claimForm);
    }) as ClaimForm | undefined;
  }

  private isEmptyClaimForm(claimForm: ClaimForm): boolean {
    const value = claimForm.value;
    return !value?.file && !value?.original_amount && !value?.due_date_at;
  }

  private async removeEmptyClaims(): Promise<void> {
    const claims = this.form.get(CLAIMS_FORM_NAME) as FormArray;
    if (!claims) {
      return;
    }

    let removed = false;

    for (let i = claims.length - 1; i >= 0; i--) {
      const claimForm = claims.at(i) as ClaimForm;
      if (!this.isEmptyClaimForm(claimForm)) {
        continue;
      }

      removed = true;
      const id = (claimForm.value as Claim).id;
      claims.removeAt(i, { emitEvent: false });
      if (id) {
        await this.deleteClaim(id);
      }
    }

    if (removed) {
      claims.updateValueAndValidity({ emitEvent: false });
    }

    this.ensureEmptyClaimForm();
  }

  private ensureEmptyClaimForm(): void {
    const claims = this.form.get(CLAIMS_FORM_NAME) as FormArray;
    if (!claims || claims.length > 0) {
      return;
    }

    claims.push((new FormFactory(this, this.fb)).getForm('claim') as ClaimForm, { emitEvent: false });
    claims.updateValueAndValidity({ emitEvent: false });
  }

  private resolveClaimFormForParsedInvoice(
    parsed: ParsedOcrInvoice,
    claims: FormArray,
  ): ClaimForm {
    if (claims.controls.includes(parsed.claimForm)) {
      return parsed.claimForm;
    }

    const invoiceNumber = parsed.invoice?.invoice_number;
    if (invoiceNumber) {
      for (const control of claims.controls) {
        const claimForm = control as ClaimForm;
        if ((claimForm.value as Claim).invoice_number === invoiceNumber) {
          return claimForm;
        }
      }
    }

    const emptyClaimForm = this.findEmptyClaimForm(claims);
    if (emptyClaimForm) {
      return emptyClaimForm;
    }

    const newClaimForm = (new FormFactory(this, this.fb)).getForm('claim') as ClaimForm;
    claims.push(newClaimForm, { emitEvent: false });
    return newClaimForm;
  }

  private async ensureJurisdictionForInvoiceOcr(
    invoice: InvoiceOcr,
    taxNumberForSearch?: string | null,
  ): Promise<void> {
    const companyData = await this.getCompanyDataForOcr(
      taxNumberForSearch ?? invoice.debtor_tax_number ?? undefined,
    );
    const newJurisdiction = companyData?.country?.iso ?? invoice.debtor_country_iso ?? null;
    const debtorForm = this.getDebtorForm();
    if (!debtorForm || !newJurisdiction) {
      return;
    }

    const currentJurisdiction = debtorForm.get('jurisdiction')?.value;
    if (newJurisdiction === currentJurisdiction) {
      return;
    }

    await this.changeJurisdiction(newJurisdiction, { emitChange: false });
    await this.reloadCurrentFormStep();
  }

  private getDebtorForm(): DebtorForm | undefined {
    return this.form.get(DEBTOR_FORM_NAME) as DebtorForm;
  }

  private getDebtorGroupKey(invoice: InvoiceOcr): string {
    const taxNumber = invoice.debtor_tax_number?.slice(0, 8);
    if (taxNumber) {
      return `tax:${taxNumber}`;
    }

    return `name:${(invoice.debtor_name ?? '').trim().toLowerCase()}`;
  }

  private buildDebtorGroups(parsedInvoices: ParsedOcrInvoice[]): OcrDebtorGroup[] {
    const groups = new Map<string, OcrDebtorGroup>();

    for (const parsedInvoice of this.getOcrParsedInvoices(parsedInvoices)) {
      const invoice = parsedInvoice.invoice!;
      const key = this.getDebtorGroupKey(invoice);
      const existingGroup = groups.get(key);

      if (existingGroup) {
        existingGroup.parsedInvoices.push(parsedInvoice);
        continue;
      }

      groups.set(key, {
        debtor_name: invoice.debtor_name,
        debtor_tax_number: invoice.debtor_tax_number,
        parsedInvoices: [parsedInvoice],
      });
    }

    return Array.from(groups.values());
  }

  private partitionOcrDebtorGroups(
    debtorGroups: OcrDebtorGroup[],
    currentDebtor: Debtor,
  ): { matched: OcrDebtorGroup[]; mismatched: OcrDebtorGroup[]; } {
    const matched: OcrDebtorGroup[] = [];
    const mismatched: OcrDebtorGroup[] = [];

    for (const group of debtorGroups) {
      if (this.isOcrGroupMatchingDebtor(group, currentDebtor)) {
        matched.push(group);
      } else {
        mismatched.push(group);
      }
    }

    return { matched, mismatched };
  }

  private isOcrGroupMatchingDebtor(
    group: OcrDebtorGroup,
    debtor: Debtor,
  ): boolean {
    const invoice = group.parsedInvoices.find((parsed) => parsed.invoice)?.invoice;
    return !!invoice && isDebtorMatchingInvoiceOcr(debtor, invoice);
  }

  private shouldOpenOcrDebtorDialog(
    debtorGroups: OcrDebtorGroup[],
    mismatched: OcrDebtorGroup[],
    currentDebtor: Debtor,
  ): boolean {
    if (!hasIdentifiedDebtor(currentDebtor)) {
      return debtorGroups.length > 1;
    }

    return mismatched.length > 0;
  }

  private getParsedInvoicesFromDebtorGroups(groups: OcrDebtorGroup[]): ParsedOcrInvoice[] {
    return groups.flatMap((group) => group.parsedInvoices);
  }

  private async openOcrDebtorDialogIfNeeded(
    currentDebtor: Debtor,
    parsedInvoices: ParsedOcrInvoice[],
    skippedFileNames: string[],
  ): Promise<boolean> {
    const debtorGroups = this.buildDebtorGroups(parsedInvoices);
    const { matched, mismatched } = this.partitionOcrDebtorGroups(
      debtorGroups,
      currentDebtor,
    );

    if (!this.shouldOpenOcrDebtorDialog(debtorGroups, mismatched, currentDebtor)) {
      return false;
    }

    const existingClaimForms = new Set(this.getNonEmptyClaimForms());
    const matchedParsed = this.getOcrParsedInvoices(
      this.getParsedInvoicesFromDebtorGroups(matched),
    );
    await this.applyParsedInvoicesToClaims(matchedParsed);

    const currentDebtorKeepClaimForms = new Set<ClaimForm>([
      ...existingClaimForms,
      ...matchedParsed.map((parsed) => parsed.claimForm),
    ]);

    await this.openMultiUploadDialog(
      {
        skippedFileNames,
        debtorGroups: mismatched,
        canKeepExistingDebtor: hasIdentifiedDebtor(currentDebtor),
        showCurrentCaseOption:
          hasIdentifiedDebtor(currentDebtor) || existingClaimForms.size > 0,
        currentCase: this.buildCurrentCaseDisplay(currentDebtor),
      },
      this.getOcrParsedInvoices(parsedInvoices),
      currentDebtorKeepClaimForms,
    );

    return true;
  }

  private buildParsedOcrInvoice(
    claimForm: ClaimForm,
    invoice: InvoiceOcr | null,
    file: File | null,
    isNewClaimForm: boolean,
  ): ParsedOcrInvoice {
    const claim = claimForm.value as Claim;
    const amount = invoice?.claim_original_amount ?? claim.original_amount ?? null;
    const currency = invoice?.currency_iso ?? claim.currency_iso ?? null;
    const dueDateValue = invoice?.claim_due_date_at ?? claim.due_date_at;

    return {
      claimForm,
      fileName: file?.name ?? this.getClaimFileName(claim),
      amountLabel: this.formatParsedInvoiceAmount(amount, currency),
      dueDate: this.formatParsedInvoiceDueDate(dueDateValue),
      invoice,
      file,
      isNewClaimForm,
    };
  }

  private async applyParsedInvoicesToClaims(parsedInvoices: ParsedOcrInvoice[]): Promise<void> {
    const claims = this.form.get(CLAIMS_FORM_NAME) as FormArray;
    if (!claims) {
      return;
    }

    for (const parsed of parsedInvoices) {
      if (!parsed.invoice || !parsed.file) {
        continue;
      }

      const claimForm = this.resolveClaimFormForParsedInvoice(parsed, claims);
      parsed.claimForm = claimForm;

      patchClaimByInvoiceOcr(claimForm, parsed.invoice, parsed.file);
    }

    await this.removeEmptyClaims();
  }

  private async reloadCurrentFormStep(): Promise<void> {
    if (!this.step) {
      return;
    }

    await this.setFormData(this.step);
    this.setFormGroup();
  }

  private async applyDebtorFromInvoiceOcr(
    invoice: InvoiceOcr,
    taxNumberForSearch?: string | null,
  ): Promise<CompanySearchAutocompleteItem | null> {
    const companyData = await this.getCompanyDataForOcr(
      taxNumberForSearch ?? invoice.debtor_tax_number ?? undefined,
    );

    const debtorForm = this.getDebtorForm();
    if (!debtorForm) {
      console.warn('Debtor form not found when applying invoice ocr');
      return companyData;
    }

    patchDebtorByInvoiceOcr(debtorForm, invoice, companyData);
    return companyData;
  }

  private async finalizeDebtorCollapseAfterCompanySearch(
    debtorForm: DebtorForm,
    companyData: CompanySearchAutocompleteItem | null,
  ): Promise<void> {
    if (await this.openInactiveCompanyDialogFromSearch(companyData)) {
      return;
    }

    debtorForm.updateValueAndValidity({ emitEvent: true });
    this.debtorCollapsed.set(debtorForm.valid);
  }

  private async openInactiveCompanyDialogFromSearch(
    companyData: CompanySearchAutocompleteItem | null,
  ): Promise<boolean> {
    if (companyData?.party_type?.type !== 'org') {
      return false;
    }

    const dialogData = getCompanyBlockDialogData(
      companyData?.status,
      companyData?.court_procedure_type,
    );
    if (!dialogData) {
      return false;
    }

    const dialogRef = this.dialog.open(DebtorCourtProcedureDialogComponent, {
      data: dialogData,
      autoFocus: false,
    });
    await firstValueFrom(dialogRef.afterClosed());
    this.debtorCollapsed.set(false);
    return true;
  }

  private buildCurrentCaseDisplay(debtor: Debtor): CurrentCaseDisplay {
    const claims = this.form.get(CLAIMS_FORM_NAME) as FormArray;
    const currentClaims: CurrentCaseClaimDisplay[] = [];

    if (claims) {
      for (const control of claims.controls) {
        const claimForm = control as ClaimForm;
        if (this.isEmptyClaimForm(claimForm)) {
          continue;
        }

        const claim = claimForm.getRawValue() as Claim;
        currentClaims.push({
          fileName: this.getClaimFileName(claim),
          amountLabel: this.formatParsedInvoiceAmount(claim.original_amount, claim.currency_iso),
          dueDate: this.formatParsedInvoiceDueDate(claim.due_date_at),
        });
      }
    }

    return {
      debtor_name: debtor.name ?? null,
      debtor_tax_number: debtor.tax_number ?? null,
      claims: currentClaims,
    };
  }

  private formatParsedInvoiceAmount(
    amount: number | null | undefined,
    currency: string | null | undefined,
  ): string {
    if (amount == null || Number.isNaN(amount)) {
      return '-';
    }

    const formatted = new Intl.NumberFormat('hu-HU', {
      useGrouping: true,
      maximumFractionDigits: 0,
    }).format(amount);

    return currency ? `${formatted} ${currency}` : formatted;
  }

  private formatParsedInvoiceDueDate(value: string | Date | null | undefined): string {
    if (!value) {
      return '-';
    }

    const date = value instanceof Date ? value : parseISO(value);
    if (!isValid(date)) {
      return '-';
    }

    return format(date, 'yyyy.MM.dd.', { locale: hu });
  }

  private getClaimFileName(claim: Claim): string {
    if (!claim.file) {
      return claim.invoice_number ?? '-';
    }

    if (claim.file instanceof File) {
      return claim.file.name;
    }

    return claim.file.file_name;
  }

  private getOcrParsedInvoices(parsedInvoices: ParsedOcrInvoice[]): ParsedOcrInvoice[] {
    return parsedInvoices.filter((parsed) => parsed.invoice && parsed.file);
  }

  private async openMultiUploadDialog(
    data: MultiUploadResultDialogData,
    allInvoices: ParsedOcrInvoice[],
    currentDebtorKeepClaimForms: Set<ClaimForm>,
  ): Promise<void> {
    const dialogRef = this.dialog.open<
      MultiUploadResultDialogComponent,
      MultiUploadResultDialogData,
      MultiUploadResultDialogResult
    >(MultiUploadResultDialogComponent, {
      data,
      autoFocus: false,
      disableClose: true,
      maxHeight: '90%',
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (!result) {
      return;
    }

    if (result.action === 'add_all_claims') {
      await this.applyOcrClaimsKeepingDebtor(allInvoices);
      return;
    }

    if (result.action === 'apply_current_debtor') {
      await this.applyOcrCurrentDebtorSelection(currentDebtorKeepClaimForms);
      return;
    }

    await this.applyOcrDebtorGroup(result.selectedGroup);
  }

  private async clearAllClaims(addEmptyClaimForm = true): Promise<void> {
    const claims = this.form.get(CLAIMS_FORM_NAME) as FormArray;
    if (!claims) {
      return;
    }

    const controls = [...claims.controls];
    for (const control of controls) {
      const claimForm = control as ClaimForm;
      const id = (claimForm.value as Claim).id;
      claims.removeAt(claims.controls.indexOf(control));
      if (id) {
        await this.deleteClaim(id);
      }
    }

    if (addEmptyClaimForm) {
      claims.push((new FormFactory(this, this.fb)).getForm('claim') as ClaimForm);
    }
  }

  private async removeClaimForms(claimForms: Set<ClaimForm>): Promise<void> {
    const claims = this.form.get(CLAIMS_FORM_NAME) as FormArray;
    if (!claims || claimForms.size === 0) {
      return;
    }

    for (let i = claims.length - 1; i >= 0; i--) {
      const claimForm = claims.at(i) as ClaimForm;
      if (!claimForms.has(claimForm)) {
        continue;
      }

      const id = (claimForm.value as Claim).id;
      claims.removeAt(i);
      if (id) {
        await this.deleteClaim(id);
      }
    }

    if (claims.length === 0) {
      claims.push((new FormFactory(this, this.fb)).getForm('claim') as ClaimForm);
    }
  }

  private async removeClaimsExcept(
    keepClaimForms: Set<ClaimForm>,
    addEmptyClaimForm = true,
  ): Promise<void> {
    const claims = this.form.get(CLAIMS_FORM_NAME) as FormArray;
    if (!claims) {
      return;
    }

    for (let i = claims.length - 1; i >= 0; i--) {
      const claimForm = claims.at(i) as ClaimForm;
      if (keepClaimForms.has(claimForm)) {
        continue;
      }

      const id = (claimForm.value as Claim).id;
      claims.removeAt(i);
      if (id) {
        await this.deleteClaim(id);
      }
    }

    if (addEmptyClaimForm && claims.length === 0) {
      claims.push((new FormFactory(this, this.fb)).getForm('claim') as ClaimForm);
    }
  }

  private showInvoiceOcrError(error: unknown): void {
    let message = this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR');
    if (error instanceof AxiosError) {
      console.error('Network error while getting invoice ocr', error.response?.data);
      if (error.response?.status === 429) {
        message = this.translate.instant('ERROR_MESSAGES.TOO_MANY_REQUESTS');
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      }
    } else {
      console.error('Unknown error while getting invoice ocr', error);
    }

    this.snackbar.open(message, this.translate.instant('OK'), { duration: 10_000 });
  }

  private async getCompanyDataForOcr(search?: string): Promise<CompanySearchAutocompleteItem | null> {
    if (!search || search.length < 3) {
      return null;
    }

    try {
      const result = await this.companySearchService.getCompanySearchAutocompleteItems(search);
      return result?.[0] ?? null;
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Network error while getting debtor company', error.response?.data);
      } else {
        console.error('Unknown error while getting debtor company', error);
      }
      return null;
    }
  }

  private async patchClientFormFromInvoiceOcr(invoiceData: InvoiceOcr): Promise<void> {
    if (this.caseId !== 'guest') {
      return;
    }

    try {
      const companyData = await this.getCompanyDataForOcr(invoiceData.client_tax_number ?? undefined);

      await this.upsertBankAccount({
        id: (new Date()).valueOf().toString(),
        account_number: null,
        iban: null,
      });

      await this.updateClient(mapInvoiceOcrToClient(invoiceData, companyData));
    } catch (error) {
      console.warn('Failed to save client by ocr', error);
    }
  }

  private async ensureClaimExchangeRatesLoaded(): Promise<void> {
    if (!this.claimExchangeRates.value) {
      return;
    }

    try {
      const result = await axios.get<{ rates: ClaimExchangeRates; }>(`${environment.baseUrl}/api/case-form/currency/exchange-rates`);
      this.claimExchangeRates.next(result.data.rates);
    } catch (error) {
      console.error('Error while loading exchange rates', error);
      this.claimExchangeRates.next(null);
    }
  }
}
